import React, { useEffect, useRef } from 'react';

/**
 * Accessible horizontal scrolling for wide tables (Phase 3 — UX-12).
 * Renders a synced scrollbar ABOVE the content so users never have to reach
 * the bottom of a long table to scroll sideways. Keyboard accessible
 * (container is focusable; arrow keys scroll).
 */
export const HScrollArea = ({ children, className = '', testid }) => {
  const topRef = useRef(null);
  const bodyRef = useRef(null);
  const ghostRef = useRef(null);

  useEffect(() => {
    const body = bodyRef.current;
    const top = topRef.current;
    const ghost = ghostRef.current;
    if (!body || !top || !ghost) return undefined;
    const sync = () => { ghost.style.width = `${body.scrollWidth}px`; top.style.display = body.scrollWidth > body.clientWidth + 2 ? 'block' : 'none'; };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(body);
    if (body.firstElementChild) ro.observe(body.firstElementChild);
    let lock = false;
    const onTop = () => { if (lock) { lock = false; return; } lock = true; body.scrollLeft = top.scrollLeft; };
    const onBody = () => { if (lock) { lock = false; return; } lock = true; top.scrollLeft = body.scrollLeft; };
    top.addEventListener('scroll', onTop);
    body.addEventListener('scroll', onBody);
    return () => { ro.disconnect(); top.removeEventListener('scroll', onTop); body.removeEventListener('scroll', onBody); };
  }, []);

  return (
    <div className={className} data-testid={testid}>
      <div ref={topRef} className="overflow-x-auto overflow-y-hidden h-3 mb-1" aria-hidden="true" style={{ display: 'none' }}>
        <div ref={ghostRef} style={{ height: 1 }} />
      </div>
      <div ref={bodyRef} tabIndex={0} className="overflow-x-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded">
        {children}
      </div>
    </div>
  );
};

export default HScrollArea;
